// frontend/src/hooks/useSiteTags.ts — the site-tags read surface (list), mirroring
// useSitePriorities. Project-scoped server state (FRONTEND-PATTERNS §State): the key
// is `['site-tags', projectId]` so a mutation's invalidate refreshes the list. The
// list row carries every field (no detail endpoint), so there is no detail hook.
// The read is member-level (a vocabulary is named by every member; defining it
// stays `reference_data:manage`).
//
// 401 is the centralized apiClient's job (ADR-003) — errors bubble so the page's
// 5-state contract renders them.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SiteTagListItem, SiteTagsListResponse } from '@ems-app/schemas/site-tags/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

export const siteTagsKeys = {
  /** List key — also the invalidation PREFIX. */
  all: (projectId: string) => ['site-tags', projectId] as const,
};

/** The list read — the page's, and a form's Load the latest (a tag has no detail read: the list row is its read). */
export async function fetchSiteTags(projectId: string): Promise<SiteTagsListResponse> {
  const r = await apiClient.get<SiteTagsListResponse>(`/projects/${projectId}/site-tags`);
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine).
const selectItems = (r: SiteTagsListResponse): SiteTagListItem[] => r.items;

export function useSiteTags(projectId: string): UseQueryResult<SiteTagListItem[], Error> {
  return useQuery({
    queryKey: siteTagsKeys.all(projectId),
    queryFn: () => fetchSiteTags(projectId),
    enabled: projectId.length > 0,
    select: selectItems,
  });
}

/** The tags as a picker's source (ADR-080 §3 — the small kind, on the list's own key): the WHOLE list on the press,
 *  alphabetical; a disabled tag is an inactive row. */
export function siteTagsSource(projectId: string): SmallSource<SiteTagsListResponse> {
  return {
    kind: 'small',
    queryKey: siteTagsKeys.all(projectId),
    fetch: () => fetchSiteTags(projectId),
    rows: (r) => r.items.map((tg) => ({ id: String(tg.id), label: tg.name, pickable: true, inactive: tg.is_disabled, group: null })),
  };
}
